import { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { MapPin, ArrowLeft, Camera, X } from 'lucide-react';

const Auth = () => {
  const { login, refreshUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Register state
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerUsername, setRegisterUsername] = useState('');
  const [registerGender, setRegisterGender] = useState('');
  const [registerAge, setRegisterAge] = useState('');
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePictureFile, setProfilePictureFile] = useState(null);

  // Check for pending profile picture from Landing page
  useEffect(() => {
    const pendingPic = sessionStorage.getItem('pendingProfilePic');
    if (pendingPic) {
      setProfilePicture(pendingPic);
      // Convert base64 to file
      fetch(pendingPic)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], 'profile.jpg', { type: 'image/jpeg' });
          setProfilePictureFile(file);
        });
      sessionStorage.removeItem('pendingProfilePic');
    }
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePictureFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicture(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePicture = () => {
    setProfilePicture(null);
    setProfilePictureFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadProfilePicture = async (token) => {
    if (!profilePictureFile) return;
    
    try {
      const formData = new FormData();
      formData.append('picture', profilePictureFile);
      
      await axios.post(`${API}/auth/upload-profile-picture`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (error) {
      console.error('Failed to upload profile picture:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: loginEmail,
        password: loginPassword
      });
      login(response.data.token, response.data.user);
      toast.success('Welcome back!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerGender) {
      toast.error('Please select your gender');
      return;
    }
    if (!registerAge || registerAge < 21) {
      toast.error('You must be at least 21 years old');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email: registerEmail,
        password: registerPassword,
        username: registerUsername,
        gender: registerGender,
        age: parseInt(registerAge)
      });
      
      // Upload profile picture if one was selected
      if (profilePictureFile) {
        await uploadProfilePicture(response.data.token);
        // Refresh user data to get updated profile picture
        if (refreshUser) {
          await refreshUser();
        }
      }
      
      login(response.data.token, response.data.user);
      toast.success('Account created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md glass border border-slate-700 p-8">
        <div className="text-center mb-8 fade-in">
          <div className="flex items-center justify-center gap-2 mb-4">
            <MapPin className="w-10 h-10 text-purple-400" />
            <h1 className="text-4xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }} data-testid="auth-title">
              Bar Buzz
            </h1>
          </div>
          <p className="text-slate-300">Find the hottest bars in your area</p>
        </div>

        <Tabs defaultValue="login" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8 bg-slate-800">
            <TabsTrigger value="login" className="data-[state=active]:bg-purple-600" data-testid="login-tab">
              Login
            </TabsTrigger>
            <TabsTrigger value="register" className="data-[state=active]:bg-purple-600" data-testid="register-tab">
              Register
            </TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="login-email" className="text-white">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="your@email.com"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  required
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <Label htmlFor="login-password" className="text-white">Password</Label>
                <Input
                  id="login-password"
                  type="password"
                  placeholder="••••••••"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  required
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="login-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={handleRegister} className="space-y-4">
              {/* Profile Picture Upload */}
              <div className="flex justify-center mb-4">
                <div 
                  className="relative w-24 h-24 cursor-pointer group"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className={`w-full h-full rounded-full border-4 ${
                    profilePicture 
                      ? 'border-purple-500' 
                      : 'border-dashed border-purple-500/50 hover:border-purple-400'
                  } transition-all flex items-center justify-center overflow-hidden bg-purple-600/20`}>
                    {profilePicture ? (
                      <img src={profilePicture} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center">
                        <Camera className="w-8 h-8 text-purple-400 mx-auto" />
                        <span className="text-xs text-purple-400">Add Photo</span>
                      </div>
                    )}
                  </div>
                  {profilePicture && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemovePicture();
                      }}
                      className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg"
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              </div>
              <p className="text-center text-xs text-slate-400 -mt-2 mb-4">Click to add a profile photo</p>
              
              <div>
                <Label htmlFor="register-username" className="text-white">Username</Label>
                <Input
                  id="register-username"
                  type="text"
                  placeholder="johndoe"
                  value={registerUsername}
                  onChange={(e) => setRegisterUsername(e.target.value)}
                  required
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="register-username-input"
                />
              </div>
              <div>
                <Label htmlFor="register-email" className="text-white">Email</Label>
                <Input
                  id="register-email"
                  type="email"
                  placeholder="your@email.com"
                  value={registerEmail}
                  onChange={(e) => setRegisterEmail(e.target.value)}
                  required
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="register-email-input"
                />
              </div>
              <div>
                <Label htmlFor="register-password" className="text-white">Password</Label>
                <Input
                  id="register-password"
                  type="password"
                  placeholder="••••••••"
                  value={registerPassword}
                  onChange={(e) => setRegisterPassword(e.target.value)}
                  required
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="register-password-input"
                />
              </div>
              <div>
                <Label htmlFor="register-age" className="text-white">Age</Label>
                <Input
                  id="register-age"
                  type="number"
                  placeholder="21+"
                  min="21"
                  value={registerAge}
                  onChange={(e) => setRegisterAge(e.target.value)}
                  required
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="register-age-input"
                />
              </div>
              <div>
                <Label htmlFor="register-gender" className="text-white">Gender</Label>
                <Select value={registerGender} onValueChange={setRegisterGender}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="register-gender-select">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="male" className="text-white">Male</SelectItem>
                    <SelectItem value="female" className="text-white">Female</SelectItem>
                    <SelectItem value="other" className="text-white">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="submit"
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </Button>
            </form>
          </TabsContent>
        </Tabs>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-slate-400 hover:text-slate-300 flex items-center gap-2 justify-center mx-auto"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
        </div>
      </Card>
    </div>
  );
};

export default Auth;
